import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Star,
  Sparkles,
  CheckCircle,
  Key,
  MessageSquare,
  MapPin,
  Tag,
  ChevronDown,
  ChevronUp,
  Calendar,
  User
} from 'lucide-react';
import ReviewForm from './ReviewForm';

interface ReviewCategory {
  name: string;
  icon: React.ReactNode;
  rating: number;
  total: number;
}

interface Review {
  id: string;
  user: {
    name: string;
    avatar?: string;
    memberSince: string;
    location?: string;
  };
  rating: number;
  comment: string;
  date: string;
  categories?: {
    [key: string]: number;
  };
}

interface ReviewsSectionProps {
  averageRating: number;
  totalReviews: number;
  reviews: Review[];
  categories: ReviewCategory[];
  onShowMore?: () => void;
  onWriteReview?: () => void;
  locationId?: string;
  locationName?: string;
}

const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  averageRating,
  totalReviews,
  reviews,
  categories,
  onShowMore,
  onWriteReview,
  locationId,
  locationName
}) => {
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());
  const [showReviewForm, setShowReviewForm] = useState(false);

  // Calculate review distribution
  const getReviewDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      distribution[review.rating as keyof typeof distribution]++;
    });
    return distribution;
  };

  const reviewDistribution = getReviewDistribution();

  const toggleReviewExpansion = (reviewId: string) => {
    const newExpanded = new Set(expandedReviews);
    if (newExpanded.has(reviewId)) {
      newExpanded.delete(reviewId);
    } else {
      newExpanded.add(reviewId);
    }
    setExpandedReviews(newExpanded);
  };

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'h-3 w-3',
      md: 'h-4 w-4',
      lg: 'h-5 w-5'
    };
    
    return (
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`${sizeClasses[size]} ${
              i < rating ? 'text-sauna-orange fill-sauna-orange' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleReviewSubmit = async (reviewData: any) => {
    // TODO: Implement review submission to backend
    console.log('Submitting review:', reviewData);
    setShowReviewForm(false);
    // You can add toast notification here
  };

  const handleReviewCancel = () => {
    setShowReviewForm(false);
  };

  return (
    <div className="space-y-6">
      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <ReviewForm
              locationId={locationId || ''}
              locationName={locationName || 'this location'}
              onSubmit={handleReviewSubmit}
              onCancel={handleReviewCancel}
            />
          </div>
        </div>
      )}

      {/* Reviews Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            {renderStars(averageRating, 'lg')}
            <span className="text-2xl font-bold text-sauna-navy">{averageRating}</span>
          </div>
          <div className="text-gray-600">
            <span className="font-medium">{totalReviews}</span> reviews
          </div>
        </div>
        
        <Button
          onClick={() => setShowReviewForm(true)}
          className="bg-sauna-orange hover:bg-sauna-orange/90 text-white"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Write Review
        </Button>
      </div>

      {/* Review Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <h3 className="font-semibold text-sauna-navy">Rating Distribution</h3>
          {[5, 4, 3, 2, 1].map((stars) => {
            const count = reviewDistribution[stars as keyof typeof reviewDistribution];
            const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
            
            return (
              <div key={stars} className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 w-8">{stars}★</span>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-sauna-orange h-2 rounded-full"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-sm text-gray-500 w-12">{count}</span>
              </div>
            );
          })}
        </div>

        {/* Category Ratings */}
        <div className="space-y-3">
          <h3 className="font-semibold text-sauna-navy">Category Ratings</h3>
          <div className="grid grid-cols-1 gap-3">
            {categories.map((category) => (
              <div key={category.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sauna-orange">{category.icon}</span>
                  <span className="text-sm text-gray-600">{category.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  {renderStars(category.rating, 'sm')}
                  <span className="text-sm text-gray-500">{category.rating}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Individual Reviews */}
      <div className="space-y-4">
        <h3 className="font-semibold text-sauna-navy">Recent Reviews</h3>
        {reviews.slice(0, showAllReviews ? reviews.length : 3).map((review) => (
          <Card key={review.id} className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={review.user.avatar} />
                  <AvatarFallback className="bg-sauna-orange/10 text-sauna-orange">
                    {getInitials(review.user.name)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-sauna-navy">{review.user.name}</h4>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span>{formatDate(review.date)}</span>
                        {review.user.location && (
                          <>
                            <span>•</span>
                            <span>{review.user.location}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {renderStars(review.rating, 'sm')}
                    </div>
                  </div>
                  
                  <div className="text-gray-700">
                    {review.comment.length > 200 && !expandedReviews.has(review.id) ? (
                      <div>
                        <p>{review.comment.substring(0, 200)}...</p>
                        <button
                          onClick={() => toggleReviewExpansion(review.id)}
                          className="text-sauna-orange hover:text-sauna-orange/80 text-sm font-medium mt-1"
                        >
                          Read more
                        </button>
                      </div>
                    ) : (
                      <p>{review.comment}</p>
                    )}
                  </div>
                  
                  {review.categories && Object.keys(review.categories).length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {Object.entries(review.categories).map(([category, rating]) => (
                        <Badge key={category} variant="outline" className="text-xs">
                          {category}: {rating}★
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {reviews.length > 3 && (
          <Button
            variant="outline"
            onClick={() => setShowAllReviews(!showAllReviews)}
            className="w-full border-sauna-orange text-sauna-orange hover:bg-sauna-orange hover:text-white"
          >
            {showAllReviews ? (
              <>
                <ChevronUp className="w-4 h-4 mr-2" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                Show All Reviews ({reviews.length})
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ReviewsSection; 